import React from 'react';
import { useTranslation } from 'react-i18next';
import { requiresRequestFlow, TDR } from 'tdr-common';
import TickCircle from '../../icons/tick-circle.svg';
import { getDisplayTime } from '../../helpers/getDisplayTime';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import { AnnouncementResponse } from '../announcements/common';
import { CardShell } from './CardShell';
import { analytics } from '../../helpers/analytics';
import { RESULT_CLICKED } from '../../helpers/analytics/constants';

type AvailableCardProps = {
    restaurant: TDR.Restaurant;
    table: TDR.Table;
    searchFilters: SearchFilters;
    announcement?: AnnouncementResponse;
    viewedTable?: boolean;
    onClick: () => void;
    timeslotData?: TDR.TimeSlotMap;
    /**
     * Resolved for this search's date and groupSize by the availability service. Deliberately
     * NOT defaulted from the table doc: it is genuinely unset while the guest is still browsing
     * without a date, and a table's default type is not what it will be on the date they pick.
     */
    bookingType?: TDR.BookingType;
    /** Start time this card represents when its section is scoped to a service. */
    slotTime?: string;
};

const AvailableFooter = ({ bookingType, currentTime, onClick }: { bookingType?: TDR.BookingType; currentTime: string; onClick: () => void }) => {
    const { t } = useTranslation();

    // Three states, not two. Without a date the availability service cannot resolve a
    // date-conditioned override, so it sends no bookingType at all -- and a table that is
    // request-only every Saturday looks instant until the guest picks one. Claiming either CTA
    // there would be a guess, so fall through to the date-less "Available" label and let the
    // real answer arrive with the date.
    // Every request-flow type (request AND inquiry) gets the request label -- an inquiry is a
    // request as far as the guest is concerned, so it must never fall through to "Available".
    const label = requiresRequestFlow({ bookingType })
        ? t('inventory.request_table')
        : bookingType === 'instant' && currentTime
            ? t('inventory.available_at', { time: getDisplayTime(currentTime) })
            : t('inventory.available');

    return (
        <div className="bg-tablz-med-gray-1 flex items-center justify-between p-3 rounded-2xl text-sm">
            <div className="flex gap-1 items-center">
                <img src={TickCircle} alt={t('aria.success_icon')} height={16} width={16} />
                <p className="text-green-400 font-poppins">
                    <span>{label}</span>
                </p>
            </div>
            <button
                type="button"
                className="font-poppins font-semibold text-[color:var(--zinc-200,#e4e4e7)] cursor-pointer"
                onClick={onClick}
                data-testid="see-details-btn"
            >
                {t('inventory.see_details')}
            </button>
        </div>
    );
};

export const AvailableCard = ({ restaurant, table, searchFilters, announcement, viewedTable, onClick, timeslotData, bookingType, slotTime }: AvailableCardProps) => {
    const handleClick = () => {
        analytics.track(RESULT_CLICKED, { resultType: 'table available' });
        onClick();
    };

    return (
        <CardShell
            restaurant={restaurant}
            table={table}
            searchFilters={searchFilters}
            announcement={announcement}
            viewedTable={viewedTable}
            onCardClick={handleClick}
            timeslotData={timeslotData}
            slotTime={slotTime}
            footer={<AvailableFooter bookingType={bookingType} currentTime={slotTime || searchFilters.time} onClick={handleClick} />}
        />
    );
};
